import { PDFDocument, StandardFonts, rgb } from 'pdf-lib';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { normalizeSportValue } from '../constants/sports';

const HOJA_TEMPLATE_URL = '/templates/hoja-registro.pdf';
const TARJETON_TEMPLATE_URL = '/templates/tarjeton-demo.pdf';

const SPORT_LABELS = {
  flag: 'Tocho Bandera',
  tackle: 'Futbol Americano'
};

const LOGO_POSITIONS = {
  hoja: {
    left: { x: 156.8, y: 106.6, w: 95.52, h: 97.28 },
    mid: { x: 315.04, y: 100.64, w: 100, h: 70.88 },
    right: { x: 608.16, y: 101.12, w: 95.36, h: 70.88 }
  },
  tarjetonFront: {
    left: { x: 40, y: 640, w: 110, h: 110 },
    mid: { x: 250, y: 640, w: 110, h: 110 },
    right: { x: 430, y: 640, w: 110, h: 110 },
    template: { x: 277.28, y: 258.08, w: 276.16, h: 192 }
  }
};

const HOJA_HEADER_POS = {
  equipo: { x: 356.04, y: 701.52 },
  deporte: { x: 125.04, y: 662.4 },
  categoria: { x: 298.2, y: 662.4 },
  rama: { x: 470.28, y: 662.4 }
};

const TARJETON_HEADER_POS = {
  equipo: { x: 166.8, y: 442.44 },
  deporte: { x: 149.4, y: 397.92 },
  categoria: { x: 295.2, y: 354.72 },
  rama: { x: 389.3, y: 397.92 }
};

const HOJA_COLUMNS = {
  numero: 65.28,
  nombre: 84.36,
  equipo: 249.96,
  anio: 315.45,
  fecha: 388.68,
  curp: 458.15
};

const HOJA_ROW_START_Y = 610.32;
const HOJA_ROW_HEIGHT = 13.5;
const HOJA_MAX_ROWS = 25;

const TARJETON_ROW_OFFSETS = {
  equipo: -10.8,
  nombre: -21.6,
  curp: -32.4,
  nivel: -43.2,
  grado: -54.0,
  playera: -64.8,
  fecha: -86.28,
  noControl: -21.6
};

const TARJETON_LEFT_X = {
  equipo: 170.28,
  nombre: 170.28,
  curp: 181.08,
  nivel: 198.12,
  grado: 220.8,
  playera: 239.52,
  fecha: 89.88,
  noControl: 170.28
};

const TARJETON_COL_GAP = 223.68;
const TARJETON_PHOTO = { x: 44, yOffset: -78, w: 52, h: 68 };

const TARJETON_PAGE1_ROWS = [543.36, 435.48, 327.6, 219.72];
const TARJETON_PAGE2_ROWS = [632.16, 524.28, 416.4, 307.2, 199.8];

const HOJA_CLEAR_BOXES = [
  { x: 340, y: 696, w: 210, h: 14 },
  { x: 115, y: 657, w: 160, h: 14 },
  { x: 285, y: 657, w: 120, h: 14 },
  { x: 455, y: 657, w: 110, h: 14 }
];

const TARJETON_FRONT_CLEAR_BOXES = [
  { x: 155, y: 438, w: 220, h: 14 },
  { x: 405, y: 438, w: 40, h: 14 },
  { x: 135, y: 394, w: 160, h: 14 },
  { x: 385, y: 394, w: 120, h: 14 },
  { x: 285, y: 350, w: 120, h: 14 }
];

const formatBirthDate = (value) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return format(date, 'd MMMM yyyy', { locale: es });
};

export const resolveSportLabel = (raw) => {
  const normalized = normalizeSportValue(raw || '');
  return SPORT_LABELS[normalized] || (raw ? String(raw) : '');
};

const resolveCategoryLabel = ({ categoria, categoria_nombre, categorias, categoria_base }) => {
  if (categoria) return categoria;
  if (categoria_nombre) return categoria_nombre;
  if (categorias?.nombre) return categorias.nombre;
  return categoria_base || '';
};

const loadImageElement = (blob) =>
  new Promise((resolve, reject) => {
    const img = new Image();
    const objectUrl = URL.createObjectURL(blob);
    img.onload = () => {
      URL.revokeObjectURL(objectUrl);
      resolve(img);
    };
    img.onerror = (err) => {
      URL.revokeObjectURL(objectUrl);
      reject(err);
    };
    img.crossOrigin = 'anonymous';
    img.src = objectUrl;
  });

const toPngBuffer = async (blob, size = 900) => {
  const img = await loadImageElement(blob);
  const canvas = document.createElement('canvas');
  const maxSize = size;
  const ratio = img.width / img.height;
  let width = maxSize;
  let height = maxSize;
  if (ratio > 1) {
    height = Math.round(maxSize / ratio);
  } else {
    width = Math.round(maxSize * ratio);
  }
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  ctx.fillStyle = '#FFFFFF';
  ctx.fillRect(0, 0, width, height);
  ctx.drawImage(img, 0, 0, width, height);
  const pngBlob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/png'));
  return await pngBlob.arrayBuffer();
};

const fetchImageBuffer = async (url, size = 900) => {
  if (!url) return null;
  const response = await fetch(url);
  if (!response.ok) return null;
  const blob = await response.blob();

  if (blob.type === 'image/png') {
    return { type: 'png', buffer: await blob.arrayBuffer() };
  }

  if (blob.type === 'image/svg+xml' || url.toLowerCase().endsWith('.svg')) {
    const svgText = await blob.text();
    const svgBlob = new Blob([svgText], { type: 'image/svg+xml' });
    const buffer = await toPngBuffer(svgBlob, size);
    return { type: 'png', buffer };
  }

  if (blob.type === 'image/jpeg' || blob.type === 'image/jpg') {
    return { type: 'jpg', buffer: await blob.arrayBuffer() };
  }

  const buffer = await toPngBuffer(blob, size);
  return { type: 'png', buffer };
};

const drawImage = async ({ pdfDoc, page, imageInfo, box }) => {
  if (!imageInfo || !box) return;
  const image = imageInfo.type === 'jpg'
    ? await pdfDoc.embedJpg(imageInfo.buffer)
    : await pdfDoc.embedPng(imageInfo.buffer);

  const ratio = image.width / image.height;
  let drawW = box.w;
  let drawH = box.h;
  if (ratio > 1) {
    drawH = box.w / ratio;
  } else {
    drawW = box.h * ratio;
  }
  const x = box.x + (box.w - drawW) / 2;
  const y = box.y + (box.h - drawH) / 2;
  page.drawImage(image, { x, y, width: drawW, height: drawH });
};

const drawText = (page, text, x, y, size, font) => {
  if (!text) return;
  page.drawText(String(text), {
    x,
    y,
    size,
    font,
    color: rgb(0, 0, 0)
  });
};

const clearBox = (page, box) => {
  if (!box) return;
  page.drawRectangle({
    x: box.x,
    y: box.y,
    width: box.w,
    height: box.h,
    color: rgb(1, 1, 1)
  });
};

const drawHojaRegistro = ({ page, font, meta, players }) => {
  const equipoLabel = meta?.equipoLabel || 'RAIDERS TRC';
  const deporteLabel = resolveSportLabel(meta?.deporte);
  const categoriaLabel = resolveCategoryLabel(meta || {});
  const ramaLabel = meta?.rama ? String(meta.rama).toUpperCase() : '';

  HOJA_CLEAR_BOXES.forEach((box) => clearBox(page, box));

  drawText(page, `EQUIPO: ${equipoLabel}`, HOJA_HEADER_POS.equipo.x, HOJA_HEADER_POS.equipo.y, 11, font);
  drawText(page, deporteLabel, HOJA_HEADER_POS.deporte.x, HOJA_HEADER_POS.deporte.y, 10, font);
  drawText(page, categoriaLabel, HOJA_HEADER_POS.categoria.x, HOJA_HEADER_POS.categoria.y, 10, font);
  drawText(page, ramaLabel, HOJA_HEADER_POS.rama.x, HOJA_HEADER_POS.rama.y, 10, font);

  const rows = players.slice(0, HOJA_MAX_ROWS);
  rows.forEach((player, index) => {
    const y = HOJA_ROW_START_Y - index * HOJA_ROW_HEIGHT;
    clearBox(page, { x: HOJA_COLUMNS.numero - 2, y: y - 2, w: 30, h: 10 });
    clearBox(page, { x: HOJA_COLUMNS.nombre - 2, y: y - 2, w: 165, h: 10 });
    clearBox(page, { x: HOJA_COLUMNS.equipo - 2, y: y - 2, w: 60, h: 10 });
    clearBox(page, { x: HOJA_COLUMNS.anio - 2, y: y - 2, w: 60, h: 10 });
    clearBox(page, { x: HOJA_COLUMNS.fecha - 2, y: y - 2, w: 70, h: 10 });
    clearBox(page, { x: HOJA_COLUMNS.curp - 2, y: y - 2, w: 120, h: 10 });
    drawText(page, player.numero || '', HOJA_COLUMNS.numero, y, 7.5, font);
    drawText(page, player.nombre_completo || '', HOJA_COLUMNS.nombre, y, 7.5, font);
    drawText(page, equipoLabel, HOJA_COLUMNS.equipo, y, 7.5, font);
    drawText(page, '', HOJA_COLUMNS.anio, y, 7.5, font);
    drawText(page, formatBirthDate(player.fecha_nacimiento), HOJA_COLUMNS.fecha, y, 7.5, font);
    drawText(page, player.curp || '', HOJA_COLUMNS.curp, y, 7.5, font);
  });
};

const drawTarjetonHeader = ({ page, font, meta, totalPlayers }) => {
  const equipoLabel = meta?.equipoLabel || 'RAIDERS TRC';
  const deporteLabel = resolveSportLabel(meta?.deporte);
  const categoriaLabel = resolveCategoryLabel(meta || {});
  const ramaLabel = meta?.rama ? String(meta.rama).toUpperCase() : '';

  TARJETON_FRONT_CLEAR_BOXES.forEach((box) => clearBox(page, box));
  drawText(page, `EQUIPO: ${equipoLabel}`, TARJETON_HEADER_POS.equipo.x, TARJETON_HEADER_POS.equipo.y, 12, font);
  drawText(page, String(totalPlayers || 0), 409.89, TARJETON_HEADER_POS.equipo.y, 12, font);
  drawText(page, deporteLabel, TARJETON_HEADER_POS.deporte.x, TARJETON_HEADER_POS.deporte.y, 10, font);
  drawText(page, categoriaLabel, TARJETON_HEADER_POS.categoria.x, TARJETON_HEADER_POS.categoria.y, 10, font);
  drawText(page, ramaLabel, TARJETON_HEADER_POS.rama.x, TARJETON_HEADER_POS.rama.y, 10, font);
};

const drawTarjetonCard = async ({ pdfDoc, page, player, rowY, isRightColumn, font, photoUrl, equipoLabel }) => {
  const colShift = isRightColumn ? TARJETON_COL_GAP : 0;
  const dataX = (key) => TARJETON_LEFT_X[key] + colShift;

  clearBox(page, { x: dataX('equipo') - 2, y: rowY + TARJETON_ROW_OFFSETS.equipo - 2, w: 140, h: 10 });
  clearBox(page, { x: dataX('nombre') - 2, y: rowY + TARJETON_ROW_OFFSETS.nombre - 2, w: 160, h: 10 });
  clearBox(page, { x: dataX('curp') - 2, y: rowY + TARJETON_ROW_OFFSETS.curp - 2, w: 170, h: 10 });
  clearBox(page, { x: dataX('nivel') - 2, y: rowY + TARJETON_ROW_OFFSETS.nivel - 2, w: 90, h: 10 });
  clearBox(page, { x: dataX('grado') - 2, y: rowY + TARJETON_ROW_OFFSETS.grado - 2, w: 140, h: 10 });
  clearBox(page, { x: dataX('playera') - 2, y: rowY + TARJETON_ROW_OFFSETS.playera - 2, w: 60, h: 10 });
  clearBox(page, { x: dataX('fecha') - 2, y: rowY + TARJETON_ROW_OFFSETS.fecha - 2, w: 120, h: 10 });
  clearBox(page, { x: dataX('noControl') - 2, y: rowY + TARJETON_ROW_OFFSETS.noControl - 2, w: 60, h: 10 });

  drawText(page, equipoLabel, dataX('equipo'), rowY + TARJETON_ROW_OFFSETS.equipo, 8, font);
  drawText(page, player?.nombre_completo || '', dataX('nombre'), rowY + TARJETON_ROW_OFFSETS.nombre, 8, font);
  drawText(page, player?.curp || '', dataX('curp'), rowY + TARJETON_ROW_OFFSETS.curp, 8, font);
  drawText(page, '', dataX('nivel'), rowY + TARJETON_ROW_OFFSETS.nivel, 8, font);
  drawText(page, '', dataX('grado'), rowY + TARJETON_ROW_OFFSETS.grado, 8, font);
  drawText(page, player?.numero || '', dataX('playera'), rowY + TARJETON_ROW_OFFSETS.playera, 8, font);
  drawText(page, formatBirthDate(player?.fecha_nacimiento), dataX('fecha'), rowY + TARJETON_ROW_OFFSETS.fecha, 8, font);

  if (photoUrl) {
    const imageInfo = await fetchImageBuffer(photoUrl, 600);
    if (imageInfo) {
      const box = {
        x: TARJETON_PHOTO.x + colShift,
        y: rowY + TARJETON_PHOTO.yOffset,
        w: TARJETON_PHOTO.w,
        h: TARJETON_PHOTO.h
      };
      await drawImage({ pdfDoc, page, imageInfo, box });
    }
  }
};

const drawTarjetonStaff = async ({ pdfDoc, page, font, headCoach, assistantCoach, equipoLabel }) => {
  if (headCoach) {
    await drawTarjetonCard({
      pdfDoc,
      page,
      player: {
        nombre_completo: headCoach?.staff?.nombre || headCoach?.staff?.nombre_completo || headCoach?.staff?.email || '',
        curp: headCoach?.staff?.curp || '',
        numero: '',
        fecha_nacimiento: ''
      },
      rowY: TARJETON_PAGE1_ROWS[0],
      isRightColumn: false,
      font,
      photoUrl: headCoach?.staff?.imagen_url || null,
      equipoLabel
    });
  }

  if (assistantCoach) {
    await drawTarjetonCard({
      pdfDoc,
      page,
      player: {
        nombre_completo:
          assistantCoach?.staff?.nombre || assistantCoach?.staff?.nombre_completo || assistantCoach?.staff?.email || '',
        curp: assistantCoach?.staff?.curp || '',
        numero: '',
        fecha_nacimiento: ''
      },
      rowY: TARJETON_PAGE1_ROWS[0],
      isRightColumn: true,
      font,
      photoUrl: assistantCoach?.staff?.imagen_url || null,
      equipoLabel
    });
  }
};

export async function buildTarjetonPdf({ players, meta, staff, fileName, preview = false }) {
  const [hojaBytes, tarjetonBytes] = await Promise.all([
    fetch(HOJA_TEMPLATE_URL).then((res) => res.arrayBuffer()),
    fetch(TARJETON_TEMPLATE_URL).then((res) => res.arrayBuffer())
  ]);

  const [hojaTemplate, tarjetonTemplate] = await Promise.all([
    PDFDocument.load(hojaBytes),
    PDFDocument.load(tarjetonBytes)
  ]);

  const pdfDoc = await PDFDocument.create();
  const font = await pdfDoc.embedFont(StandardFonts.Helvetica);

  const hojaChunks = [];
  for (let i = 0; i < players.length; i += HOJA_MAX_ROWS) {
    hojaChunks.push(players.slice(i, i + HOJA_MAX_ROWS));
  }
  if (hojaChunks.length === 0) hojaChunks.push([]);

  const hojaPages = [];
  for (const chunk of hojaChunks) {
    const [hojaPage] = await pdfDoc.copyPages(hojaTemplate, [0]);
    pdfDoc.addPage(hojaPage);
    drawHojaRegistro({ page: hojaPage, font, meta, players: chunk });
    hojaPages.push(hojaPage);
  }

  const [tarjetonFront] = await pdfDoc.copyPages(tarjetonTemplate, [3]);
  pdfDoc.addPage(tarjetonFront);
  clearBox(tarjetonFront, LOGO_POSITIONS.tarjetonFront.template);
  drawTarjetonHeader({ page: tarjetonFront, font, meta, totalPlayers: players.length });

  const [tarjetonFirstReverse] = await pdfDoc.copyPages(tarjetonTemplate, [0]);
  pdfDoc.addPage(tarjetonFirstReverse);

  const equipoLabel = meta?.equipoLabel || 'RAIDERS TRC';
  await drawTarjetonStaff({
    pdfDoc,
    page: tarjetonFirstReverse,
    font,
    headCoach: staff?.headCoach,
    assistantCoach: staff?.assistantCoach,
    equipoLabel
  });

  const remainingPlayers = players.slice();
  const firstPagePlayers = remainingPlayers.splice(0, 6);
  for (let i = 0; i < firstPagePlayers.length; i += 1) {
    const rowIndex = Math.floor(i / 2) + 1;
    const isRight = i % 2 === 1;
    const rowY = TARJETON_PAGE1_ROWS[rowIndex];
    if (rowY) {
      await drawTarjetonCard({
        pdfDoc,
        page: tarjetonFirstReverse,
        player: firstPagePlayers[i],
        rowY,
        isRightColumn: isRight,
        font,
        photoUrl: firstPagePlayers[i]?.imagen_url || null,
        equipoLabel
      });
    }
  }

  while (remainingPlayers.length > 0) {
    const [page] = await pdfDoc.copyPages(tarjetonTemplate, [1]);
    pdfDoc.addPage(page);
    const chunk = remainingPlayers.splice(0, 10);
    for (let i = 0; i < chunk.length; i += 1) {
      const rowIndex = Math.floor(i / 2);
      const isRight = i % 2 === 1;
      const rowY = TARJETON_PAGE2_ROWS[rowIndex];
      if (rowY) {
        await drawTarjetonCard({
          pdfDoc,
          page,
          player: chunk[i],
          rowY,
          isRightColumn: isRight,
          font,
          photoUrl: chunk[i]?.imagen_url || null,
          equipoLabel
        });
      }
    }
  }

  const [raidersLogo, torneoLogo, ligaLogo] = await Promise.all([
    fetchImageBuffer(meta?.clubLogoUrl || '/assets/raiders-trc-icon.svg', 900),
    fetchImageBuffer(meta?.torneoLogoUrl || null, 900),
    fetchImageBuffer(meta?.ligaLogoUrl || null, 900)
  ]);

  if (raidersLogo) {
    for (const hojaPage of hojaPages) {
      clearBox(hojaPage, LOGO_POSITIONS.hoja.left);
      await drawImage({ pdfDoc, page: hojaPage, imageInfo: raidersLogo, box: LOGO_POSITIONS.hoja.left });
    }
    await drawImage({ pdfDoc, page: tarjetonFront, imageInfo: raidersLogo, box: LOGO_POSITIONS.tarjetonFront.left });
  }

  if (torneoLogo || ligaLogo) {
    const firstLogo = torneoLogo || ligaLogo;
    const secondLogo = ligaLogo || torneoLogo;
    if (firstLogo) {
      for (const hojaPage of hojaPages) {
        clearBox(hojaPage, LOGO_POSITIONS.hoja.mid);
        await drawImage({ pdfDoc, page: hojaPage, imageInfo: firstLogo, box: LOGO_POSITIONS.hoja.mid });
      }
      await drawImage({ pdfDoc, page: tarjetonFront, imageInfo: firstLogo, box: LOGO_POSITIONS.tarjetonFront.mid });
    }
    if (secondLogo) {
      for (const hojaPage of hojaPages) {
        clearBox(hojaPage, LOGO_POSITIONS.hoja.right);
        await drawImage({ pdfDoc, page: hojaPage, imageInfo: secondLogo, box: LOGO_POSITIONS.hoja.right });
      }
      await drawImage({ pdfDoc, page: tarjetonFront, imageInfo: secondLogo, box: LOGO_POSITIONS.tarjetonFront.right });
    }
  }

  const pdfBytes = await pdfDoc.save();
  const blob = new Blob([pdfBytes], { type: 'application/pdf' });

  if (preview) {
    return blob;
  }

  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName || 'tarjeton_ltl_kids.pdf';
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}
